import {Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {AppConfigurationService} from '../../common/app-config/app-configuration.service';
import {AuthToken, IDictionary, ILoggedInPatient} from '../common/common.types';
import { map } from 'rxjs/operators';
import {AuthProviderService} from './auth-provider.service';
import {LogManager} from '../../common/log/log.manager';
import {LogCat} from '../../common/log/log.categories';
import {IntlServicePP} from '../../common/intl/services/intl.service';
import {PatientAccountPreferencesService} from './patient.account.preferences.service';
import {PatientService} from "./patient.service";

@Injectable({
  providedIn: 'root'
})
export class LoginExternalService {

  constructor(private httpClient: HttpClient,
              private configService: AppConfigurationService,
              private tokenProvider: AuthProviderService,
              private patientService: PatientService,
              private intlService: IntlServicePP,
              private patientAccountPreferencesService: PatientAccountPreferencesService) {

  }

  public login(param: IDictionary<string>): Promise<boolean> {
    return this.doLogin(JSON.stringify(param), '/OAuth/PatientPortalExternalAuth');
  }

  public loginWithToken(token: AuthToken): Promise<boolean> {
    return this.doLogin(token.access_token, '/OAuth/PatientPortalTokenAuth');
  }

  private async doLogin(userData: string, authMethod: string): Promise<boolean> {
    const config =  this.configService.Config;

    const xmlRequestParam = encodeURIComponent('userName') + '=' +
      encodeURIComponent(userData) + '&' +
      encodeURIComponent('password') + '=' + encodeURIComponent('') + '&' +
      encodeURIComponent('grant_type') + '=' + encodeURIComponent('password');

    const reqSpec = {
      headers: new HttpHeaders(
        {
          ['Content-Type']: 'application/x-www-form-urlencoded;charset=utf-8'
        })
    };

    const patientSubj = await this.httpClient.post<AuthToken>(config.AuthServerUrl + authMethod, xmlRequestParam, reqSpec)
      .pipe(map((newToken: AuthToken) => {
        if (newToken && newToken.access_token) {
          return this.tokenProvider.setCredentials(newToken);
        }
        return  null;
      })).toPromise();

    if (patientSubj) {
      const identity = await this.patientService.getPatientDetails().toPromise();

      // If this is the first user login, currently selected system language will be set as user's default language
      if (identity.accountPreferences.defaultLanguageKey === 0 && this.intlService.usedLanguage) {
        this.patientAccountPreferencesService.setDefaultAccountLanguage(this.intlService.usedLanguage.languageKey);
      } else if (identity.accountPreferences.defaultLanguageKey !== this.intlService.usedLanguage.languageKey) {
        // If the system language was changed before login and it is different from user's default language - user language will be updated
        if (this.intlService.defaultBrowserLanguage && this.intlService.usedLanguage &&
          this.intlService.defaultBrowserLanguage.languageKey !== this.intlService.usedLanguage.languageKey) {
          this.patientAccountPreferencesService.setDefaultAccountLanguage(this.intlService.usedLanguage.languageKey);
        } else {
          const userLang = this.intlService.cachedLanguages.find(l => l.languageKey === identity.accountPreferences.defaultLanguageKey);
          if (userLang) {
            this.intlService.setLanguage(userLang, true);
          }
        }
      }

      patientSubj.next(identity);

      LogManager.debug(LogCat.Login.ExternalLogin, `login succeeded for patient - ${identity}`);
      return true;
    } else {
      return false;
    }
  }
}
